import { useState, useRef } from 'react'
import { Link, router } from '@inertiajs/react'
import { ResultsOwnershipChartPlacement, PersonSummary, ROLE_LABELS, roleOptionsForAssignment, UserRole } from '../../types'
import UserAvatar from '../shared/UserAvatar'
import FloatingMenu from '../shared/FloatingMenu'
import DeactivateConfirmModal from './DeactivateConfirmModal'
import ChartPlacementModal from './ChartPlacementModal'
import { LABELS } from '../../pages/AccountabilityCharts/labels'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  user: PersonSummary
  canManage: boolean
  managers: { id: number; name: string }[]
  /** Roles the current viewer is allowed to assign when restoring a removed person. */
  restoreRoles?: string[]
  /** Roles the current viewer is allowed to assign when changing someone's role. */
  assignableRoles?: string[]
  /** When on, the row shows whether this person owns a role on the Results Ownership Chart. */
  showChartStatus?: boolean
  /** The org's active chart, when one is visible to the viewer. */
  chart?: ResultsOwnershipChartPlacement | null
}

const statusStyles: Record<string, string> = {
  active: 'bg-[rgba(27,115,64,0.15)] text-[#1B7340]',
  deactivated: 'bg-charcoal/10 text-text-secondary',
  deleted: 'bg-[rgba(237,28,36,0.1)] text-[#B01116]',
}

export default function UserRow({ user, canManage, managers, restoreRoles = [], assignableRoles = [], showChartStatus = false, chart = null }: Props) {
  const [saving, setSaving] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showDeactivate, setShowDeactivate] = useState(false)
  const [showPlacement, setShowPlacement] = useState(false)
  const [reactivating, setReactivating] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Inline row edits must never fail quietly: a rejected change leaves the
  // select showing the old value, so without this the click looks like a no-op.
  const saveField = (field: string, value: string) => {
    setSaving(true)
    setSaveError(null)
    router.put(`/people/${user.id}`, { user: { [field]: value } }, {
      preserveScroll: true,
      onError: errors => {
        const message = Object.values(errors).filter(Boolean).join(', ')
        setSaveError(message || 'Could not save that change.')
      },
      onFinish: () => setSaving(false),
    })
  }

  const isGuide = user.role === 'guide'
  const isDeleted = user.status === 'deleted'
  // A deleted person has no seat to hold; everyone else is placeable as long as
  // the viewer may edit the chart, whether or not they already own a role.
  const canPlaceOnChart = Boolean(chart?.can_place) && !isDeleted
  const [restoreRole, setRestoreRole] = useState<string>(
    restoreRoles.includes(user.role) ? user.role : 'member'
  )
  const [restoring, setRestoring] = useState(false)

  const handleRestore = () => {
    setRestoring(true)
    setSaveError(null)
    router.post(`/people/${user.id}/restore`, { role: restoreRole }, {
      preserveScroll: true,
      onError: errors => {
        const message = Object.values(errors).filter(Boolean).join(', ')
        setSaveError(message || 'Could not restore this person.')
      },
      onFinish: () => setRestoring(false),
    })
  }

  const toggleGuidePrivileges = () => {
    setSaving(true)
    setSaveError(null)
    router.patch(`/people/${user.id}/guide_privileges`, {
      guide_user_privileges: !user.guide_user_privileges,
    }, {
      preserveScroll: true,
      onError: errors => {
        const message = Object.values(errors).filter(Boolean).join(', ')
        setSaveError(message || 'Could not save that change.')
      },
      onFinish: () => setSaving(false),
    })
  }

  const handleReactivate = () => {
    setMenuOpen(false)
    setReactivating(true)
    router.post(`/people/${user.id}/reactivate`, {}, {
      preserveScroll: true,
      onFinish: () => setReactivating(false),
    })
  }

  return (
    <>
      <div className={`flex items-center gap-3 px-4 py-3 border-b border-stone/10 last:border-0 transition-colors ${saving || reactivating ? 'opacity-60' : 'hover:bg-stone/5'}`}>
        <Link href={`/people/${user.id}`} className="flex items-center gap-3 flex-1 min-w-0">
          <UserAvatar
            user={{ avatar_display: user.avatar_text || user.initials, avatar_url: user.avatar_url, full_name: user.full_name }}
            size="sm"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-text-primary truncate">{user.full_name}</span>
              {user.is_executive_leadership && (
                <span className="text-[12px] font-semibold bg-[#B87A20]/20 text-[#B87A20] px-1.5 py-0.5 rounded-full uppercase tracking-wide shrink-0">ELT</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-sm text-text-secondary truncate">{user.email}</span>
              {user.title && (
                <>
                  <span className="text-text-muted shrink-0">·</span>
                  <span className="text-sm text-text-secondary truncate">{user.title}</span>
                </>
              )}
            </div>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-1.5 shrink-0">
          {user.teams.length > 0 && (
            <span className="text-sm text-text-muted bg-stone/20 px-2 py-0.5 rounded-full">
              {user.teams.length} team{user.teams.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {showChartStatus && (
          <div className="flex items-center shrink-0" onClick={e => e.stopPropagation()}>
            {canPlaceOnChart ? (
              // Placement stays available to someone already on the chart: one
              // person can own several roles, so the badge doubles as the way
              // to give them another one.
              <button
                onClick={() => setShowPlacement(true)}
                title={
                  user.on_chart
                    ? `${(user.chart_seat_names || []).join(', ')} — add another ${LABELS.seat.toLowerCase()}`
                    : `Place ${user.full_name} on the ${LABELS.feature}`
                }
                aria-label={
                  user.on_chart
                    ? `${user.full_name} is on the ${LABELS.feature} — add another ${LABELS.seat.toLowerCase()}`
                    : `Add ${user.full_name} to the ${LABELS.feature}`
                }
                className={`text-[12px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap transition-colors ${
                  user.on_chart
                    ? 'bg-sea/15 text-sea hover:bg-sea/25'
                    : 'bg-stone text-text-primary hover:bg-stone/70'
                }`}
              >
                {user.on_chart ? `On ${LABELS.feature}` : `Add to ${LABELS.feature}`}
              </button>
            ) : (
              <span
                className={`text-[12px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                  user.on_chart ? 'bg-sea/15 text-sea' : 'bg-stone text-text-primary'
                }`}
                title={user.on_chart ? (user.chart_seat_names || []).join(', ') : undefined}
              >
                {user.on_chart ? `On ${LABELS.feature}` : `Not on ${LABELS.feature}`}
              </span>
            )}
          </div>
        )}

        {canManage && isDeleted ? (
          <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
            {isGuide ? (
              <span className="text-sm text-text-muted whitespace-nowrap" title="Removed Guides are restored through the Add Guide flow.">
                Restore via Add Guide
              </span>
            ) : (
              <>
                <select
                  value={restoreRole}
                  onChange={e => setRestoreRole(e.target.value)}
                  disabled={restoring}
                  className="border border-stone/30 rounded-lg px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-charcoal/40 focus:border-charcoal bg-white"
                >
                  {restoreRoles.map(role => (
                    <option key={role} value={role}>{ROLE_LABELS[role as UserRole] || role}</option>
                  ))}
                </select>
                <button
                  onClick={handleRestore}
                  disabled={restoring}
                  className="px-3 py-1 text-sm font-semibold rounded-lg text-[#15532E] border border-[#15532E]/30 hover:bg-[rgba(21,83,46,0.1)] transition-colors whitespace-nowrap"
                >
                  {restoring ? 'Restoring…' : 'Restore'}
                </button>
              </>
            )}
          </div>
        ) : canManage && isGuide ? (
          /* Guide details are managed by Pinnacle admins; the only tenant-level
             control is the per-org "Guide user privileges" opt-in. */
          <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
            <span className="text-sm text-text-secondary bg-stone/20 px-2 py-0.5 rounded-full">
              {ROLE_LABELS[user.role as UserRole] || user.role}
            </span>
            <label
              className="flex items-center gap-1.5 cursor-pointer select-none border border-stone/30 rounded-lg px-2 py-1 bg-white"
              title="When on, this guide can own Topics, Rocks, and Action Items and appears in meeting attendance for this account."
            >
              <input
                type="checkbox"
                checked={!!user.guide_user_privileges}
                onChange={toggleGuidePrivileges}
                disabled={saving}
                className="w-4 h-4 rounded border-stone/30 text-pinnacle-red focus:ring-pinnacle-red/20"
              />
              <span className="text-sm text-text-primary whitespace-nowrap">User privileges</span>
            </label>
          </div>
        ) : canManage ? (
          <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
            <select
              value={user.role}
              onChange={e => saveField('role', e.target.value)}
              className="border border-stone/30 rounded-lg px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-charcoal/40 focus:border-charcoal bg-white"
            >
              {roleOptionsForAssignment(assignableRoles, user.role as UserRole).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <select
              value={user.manager_id ?? ''}
              onChange={e => saveField('manager_id', e.target.value)}
              className="border border-stone/30 rounded-lg px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-charcoal/40 focus:border-charcoal bg-white max-w-[130px]"
            >
              <option value="">No manager</option>
              {sortPeople(managers.filter(m => m.id !== user.id)).map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-text-secondary bg-stone/20 px-2 py-0.5 rounded-full">
              {ROLE_LABELS[user.role as UserRole] || user.role}
            </span>
            {user.manager_name && (
              <span className="text-sm text-text-muted max-w-[120px] truncate" title={`Reports to ${user.manager_name}`}>
                → {user.manager_name}
              </span>
            )}
          </div>
        )}

        <span className={`text-[12px] font-medium px-2 py-0.5 rounded-full shrink-0 ${statusStyles[user.status] || ''}`}>
          {user.status}
        </span>

        {canManage && !isDeleted ? (
          <div className="shrink-0" onClick={e => e.stopPropagation()}>
            <button
              ref={triggerRef}
              onClick={() => setMenuOpen(o => !o)}
              className="p-1 rounded hover:bg-stone/20 transition-colors"
              aria-label="Actions"
            >
              <svg className="w-4 h-4 text-text-muted" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
            <FloatingMenu
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              anchorRef={triggerRef}
              width={176}
              onClick={e => e.stopPropagation()}
              className="w-44 bg-white rounded-lg shadow-elevated border border-stone/20 py-1"
            >
                <Link
                  href={`/people/${user.id}`}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-stone/10 transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  <svg className="w-3.5 h-3.5 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  View Profile
                </Link>

                {user.status === 'active' ? (
                  <button
                    onClick={() => { setMenuOpen(false); setShowDeactivate(true) }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#B87A20] hover:bg-[#B87A20]/5 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    Deactivate
                  </button>
                ) : (
                  <button
                    onClick={handleReactivate}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#15532E] hover:bg-[rgba(21,83,46,0.15)] transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Reactivate
                  </button>
                )}

                {user.role !== 'account_owner' && (
                  <Link
                    href={`/people/${user.id}/deletion_manifest`}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#ED1C24] hover:bg-[rgba(237,28,36,0.15)] transition-colors"
                    onClick={() => setMenuOpen(false)}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete User
                  </Link>
                )}
            </FloatingMenu>
          </div>
        ) : (
          <Link href={`/people/${user.id}`} className="shrink-0 p-0.5">
            <svg className="w-4 h-4 text-text-muted hover:text-text-secondary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>

      {saveError && (
        <div
          role="alert"
          className="flex items-start gap-2 px-4 py-2 border-b border-stone/10 bg-[rgba(237,28,36,0.08)] text-sm text-[#B01116]"
        >
          <span className="flex-1">{saveError}</span>
          <button
            onClick={() => setSaveError(null)}
            className="shrink-0 font-semibold hover:underline"
            aria-label="Dismiss error"
          >
            Dismiss
          </button>
        </div>
      )}

      {showDeactivate && (
        <DeactivateConfirmModal user={user} onClose={() => setShowDeactivate(false)} />
      )}

      {showPlacement && chart && (
        <ChartPlacementModal
          chart={chart}
          person={{ id: user.id, full_name: user.full_name }}
          onClose={() => setShowPlacement(false)}
        />
      )}
    </>
  )
}
